import { useState, useMemo } from 'react';

export interface UsePaginationOptions {
  initialPage?: number;
  initialItemsPerPage?: number;
  itemsPerPageOptions?: number[];
}

export interface UsePaginationReturn {
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (itemsPerPage: number) => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  itemsPerPageOptions: number[];
}

export function usePagination<T>(
  items: T[] | undefined | null,
  options: UsePaginationOptions = {}
): UsePaginationReturn & { paginatedItems: T[] } {
  const {
    initialPage = 1,
    initialItemsPerPage = 25,
    itemsPerPageOptions = [10, 25, 50, 100],
  } = options;

  // Asegurar que items sea siempre un array
  const safeItems = Array.isArray(items) ? items : [];

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);

  const totalPages = Math.max(1, Math.ceil(safeItems.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Asegurar que currentPage no exceda totalPages
  const validCurrentPage = Math.min(currentPage, totalPages);

  const paginatedItems = useMemo(() => {
    return safeItems.slice(startIndex, endIndex);
  }, [safeItems, startIndex, endIndex]);

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToNextPage = () => {
    if (validCurrentPage < totalPages) {
      setCurrentPage(validCurrentPage + 1);
    }
  };
  const goToPreviousPage = () => {
    if (validCurrentPage > 1) {
      setCurrentPage(validCurrentPage - 1);
    }
  };

  // Resetear a página 1 si la página actual es mayor que totalPages
  if (validCurrentPage !== currentPage) {
    setCurrentPage(1);
  }

  return {
    currentPage: validCurrentPage,
    itemsPerPage,
    totalPages,
    startIndex,
    endIndex,
    setCurrentPage,
    setItemsPerPage: (newItemsPerPage: number) => {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1); // Resetear a primera página al cambiar itemsPerPage
    },
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPreviousPage,
    itemsPerPageOptions,
    paginatedItems,
  };
}
